import { useEffect } from 'react';
import { SITE_URL } from '../constants';

const toAbsolute = (value) =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    ? `${SITE_URL}${value}`
    : value;

const resolveSchema = (value) => {
  if (typeof value === 'string') return toAbsolute(value);
  if (Array.isArray(value)) return value.map(resolveSchema);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, resolveSchema(item)])
    );
  }
  return value;
};

export const useSEO = (seo) => {
  useEffect(() => {
    // Update title
    document.title = seo.title;

    // Update or create description meta tag
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.name = 'description';
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.content = seo.description;

    // Remove keywords meta tag if exists (obsolete)
    const keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (keywordsMeta) {
      keywordsMeta.remove();
    }

    // Update Open Graph tags
    const ogTags = {
      'og:type': seo.openGraph.type,
      'og:url': toAbsolute(seo.openGraph.url),
      'og:title': seo.openGraph.title,
      'og:description': seo.openGraph.description,
      'og:image': toAbsolute(seo.openGraph.image),
    };

    Object.entries(ogTags).forEach(([property, content]) => {
      let ogMeta = document.querySelector(`meta[property="${property}"]`);
      if (!ogMeta) {
        ogMeta = document.createElement('meta');
        ogMeta.property = property;
        document.head.appendChild(ogMeta);
      }
      ogMeta.content = content;
    });

    // Update Twitter tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:url': toAbsolute(seo.openGraph.url),
      'twitter:title': seo.openGraph.title,
      'twitter:description': seo.openGraph.description,
      'twitter:image': toAbsolute(seo.openGraph.image),
    };

    Object.entries(twitterTags).forEach(([name, content]) => {
      let twitterMeta = document.querySelector(`meta[name="${name}"]`);
      if (!twitterMeta) {
        twitterMeta = document.createElement('meta');
        twitterMeta.name = name;
        document.head.appendChild(twitterMeta);
      }
      twitterMeta.content = content;
    });

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = toAbsolute(seo.openGraph.url);

    // Add structured data (Schema.org)
    if (seo.schemaLD) {
      let schemaScript = document.getElementById('structured-data');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'structured-data';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(resolveSchema(seo.schemaLD));
    }

    // Cleanup on unmount
    return () => {
      // Restore default title if needed
    };
  }, [seo]);
};
